import { type AxiosResponse } from 'axios'
import { type AxiosOptions } from 'shared/api'
import { API_URL_MICROSERVICE } from 'shared/config'
import { baseApi } from 'entities/Auth'
import {
  type IParamsResetPasswordMembers,
  type IBodyUpdateAwayStatus,
  type IBodyUpdateUsersTeam,
  type IParamsCalculateInvitesSeats,
  type IParamsCancelInvite,
  type IParamsChangeUserPassword,
  type IParamsCreateInvites,
  type IParamsDeleteMember,
  type IParamsLogoutMembers,
  type IParamsOrganizationInvites,
  type IParamsGetOrganizationMembers,
  type IParamsOrganizationMembers,
  type IParamsResendInvites,
  type IParamsUpdateMember,
  type IParamsUpdateMessageSignature,
  type IParamsUpdateUsersProfilesUiSettings,
  type IParamsUpdateUsersUiSettings,
  type IParamsUpdateQuietHoursUiSettings,
  type IParamsUserAccessRequestsList,
  type IParamsUsersSearch,
  type IParamsUsersToggleRecordAutomatically,
  type IParamsUsersToggleRingEverywhere,
  type IParamsSetOptionsNotifications,
  type IResponseAwayStatus,
  type IResponseSetOptionsNotifications,
  type IResponseBusinessHours,
  type IResponseCalculateInvitesSeats,
  type IResponseChangeUserEmail,
  type IResponseChangeUserPassword,
  type IResponseMembersCounts,
  type IResponseMessageSignatureAnswer,
  type IResponseOrganizationInvites,
  type IResponseOrganizationMembers,
  type IResponseOwnerUser,
  type IResponseProfile,
  type IResponseRequestInboxAccess,
  type IResponseUiSettings,
  type IResponseUiSettingsHubspotAllEntities,
  type IResponseQuietHoursUiSettings,
  type IResponseUpdateUsersTeam,
  type IResponseUser,
  type IResponseUsersAccessRequestsList,
  type IResponseUsersMe,
  type IResponseUsersProfilesUiSettings,
  type IResponseUsersSearch,
  type IUiSettingsSourceType,
  type IUsersProfileUiSettingsSourceType,
  type IResponseCancelInvite,
  type IParamsUpdateUserProfile,
  type IParamsUpdateEmail,
  type IParamsUpdatePassword,
  type IResponseResendInvite,
  type IResponseAssumeUser,
  type IResponseOrganizationInvitesAll,
  type IParamsUpdateUsersProfilePhone,
} from 'entities/Users/api/types'
import { type IResponseOwner } from 'entities/Referrals'

class Api {
  getUsersMe(): Promise<AxiosResponse<IResponseUsersMe>> {
    return baseApi.get('users/me')
  }

  getUser(): Promise<AxiosResponse<IResponseOwnerUser>> {
    return baseApi.get<IResponseOwnerUser>('user').then((response) => {
      response.data.item_type = 'owner'

      return response
    })
  }

  getUserById(id: number): Promise<AxiosResponse<IResponseUser>> {
    return baseApi.get(`user/${id}`)
  }

  getUsers(): Promise<AxiosResponse<IResponseUser[]>> {
    return baseApi.get('users')
  }

  toggleAwayStatus(body: IBodyUpdateAwayStatus): Promise<AxiosResponse<IResponseAwayStatus>> {
    return baseApi.post('users/away-status', body)
  }

  updateUsersTeam(
    body: IBodyUpdateUsersTeam,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseUpdateUsersTeam>> {
    return baseApi.put('users/team', body, options)
  }

  getUsersSearch(
    params: IParamsUsersSearch,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseUsersSearch>> {
    return baseApi.get('users/search', { ...options, params })
  }

  getUsersBusinessHours(): Promise<AxiosResponse<IResponseBusinessHours>> {
    return baseApi.get('users/business-hours')
  }

  updateUsersToggleRecordAutomatically(params: IParamsUsersToggleRecordAutomatically) {
    return baseApi.post('users/toggle-record-automatically', params)
  }

  updateUsersToggleRingEverywhere(params: IParamsUsersToggleRingEverywhere) {
    return baseApi.post('users/toggle-ring-everywhere', params)
  }

  setOptionsNotifications(
    params: IParamsSetOptionsNotifications
  ): Promise<AxiosResponse<IResponseSetOptionsNotifications>> {
    return baseApi.patch('users/settings-multiple', params)
  }

  getMessageSignature(): Promise<AxiosResponse<IResponseMessageSignatureAnswer>> {
    return baseApi.get('users/message-signature')
  }

  updateMessageSignature(
    params: IParamsUpdateMessageSignature
  ): Promise<AxiosResponse<IResponseMessageSignatureAnswer>> {
    return baseApi.post('users/message-signature', params)
  }

  getUsersProfilesUiSettings(
    source_type: IUsersProfileUiSettingsSourceType
  ): Promise<AxiosResponse<IResponseUsersProfilesUiSettings>> {
    return baseApi.get(`users/profiles/ui-settings/${source_type}`)
  }

  updateUsersProfilesUiSettings(
    params: IParamsUpdateUsersProfilesUiSettings
  ): Promise<AxiosResponse<IResponseUsersProfilesUiSettings>> {
    return baseApi.post('users/profiles/ui-settings/sync', params)
  }

  getUsersUiSettings(
    source_type: IUiSettingsSourceType,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseUiSettings>> {
    return baseApi.get(`users/ui-settings/${source_type}`, options)
  }

  getQuietHoursUiSettings(
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseQuietHoursUiSettings>> {
    return baseApi.get('users/ui-settings/quite-hours-items', options)
  }

  getUsersUiSettingsHubspotAllEntities(): Promise<
    AxiosResponse<IResponseUiSettingsHubspotAllEntities>
  > {
    return baseApi.get('users/ui-settings/hubspot-all-entities-draggable-items')
  }

  updateUsersUiSettings(
    params: IParamsUpdateUsersUiSettings
  ): Promise<AxiosResponse<IResponseUiSettings>> {
    return baseApi.post('users/ui-settings/sync', params)
  }

  updateQuietHoursUiSettings(
    params: IParamsUpdateQuietHoursUiSettings
  ): Promise<AxiosResponse<IResponseUiSettings>> {
    return baseApi.post('users/ui-settings/sync', params)
  }

  getRequestInboxAccess(): Promise<AxiosResponse<IResponseRequestInboxAccess>> {
    return baseApi.get('users/assign-inbox-request')
  }

  updateRequestInboxAccess(): Promise<AxiosResponse<IResponseRequestInboxAccess>> {
    return baseApi.post('users/assign-inbox-request')
  }

  getUsersProfile(): Promise<AxiosResponse<IResponseProfile>> {
    return baseApi.get('users/profile')
  }

  updateUsersProfilePhone(
    params: IParamsUpdateUsersProfilePhone
  ): Promise<AxiosResponse<IResponseOwner>> {
    return baseApi.post('users/profile/number', params)
  }

  updateUsersProfile(params: IParamsUpdateUserProfile): Promise<AxiosResponse<IResponseProfile>> {
    return baseApi.post('users/profile', params)
  }

  changeUserEmail({
    id,
    email,
  }: {
    id: number
    email: string
  }): Promise<AxiosResponse<IResponseChangeUserEmail>> {
    return baseApi.post(`admin/core/users/${id}/change-email`, {
      email,
    })
  }

  changeUserPassword({
    id,
    params,
  }: {
    id: number
    params: IParamsChangeUserPassword
  }): Promise<AxiosResponse<IResponseChangeUserPassword>> {
    return baseApi.post(`admin/core/users/${id}/change-password`, params)
  }

  getOrganizationMembers(
    params?: IParamsGetOrganizationMembers
  ): Promise<AxiosResponse<IResponseUser[]>> {
    return baseApi.get('organization/members', { params })
  }

  getOrganizationActiveMembersList(
    params: IParamsOrganizationMembers,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseOrganizationMembers>> {
    return baseApi.get('organization/members/list', { params, ...options })
  }

  getOrganizationPendingInvitesList(
    params: IParamsOrganizationInvites,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseOrganizationInvites>> {
    return baseApi.get('organization/invites/list', { params, ...options })
  }

  getOrganizationPendingInvitesAll(): Promise<AxiosResponse<IResponseOrganizationInvitesAll>> {
    return baseApi.get('organization/invites')
  }

  getUsersAccessRequestsList(
    params: IParamsUserAccessRequestsList,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseUsersAccessRequestsList>> {
    return baseApi.get('users/access-requests/list', { params, ...options })
  }

  getMembersCounts(): Promise<AxiosResponse<IResponseMembersCounts>> {
    return baseApi.get('organization/members/counts')
  }

  deleteMember(id: number, params: IParamsDeleteMember): Promise<AxiosResponse> {
    return baseApi.delete(`organization/members/${id}`, { params })
  }

  resendInvite(params: IParamsResendInvites): Promise<AxiosResponse<IResponseResendInvite>> {
    return baseApi.post('organization/invites/bulk/resend', params)
  }

  cancelInvite(params: IParamsCancelInvite): Promise<AxiosResponse<IResponseCancelInvite>> {
    return baseApi.post('organization/invites/bulk/revoke', params)
  }

  calculateInvitesSeats(
    params: IParamsCalculateInvitesSeats
  ): Promise<AxiosResponse<IResponseCalculateInvitesSeats>> {
    return baseApi.post('organization/invites/seats/count', params)
  }

  logoutMembers(params: IParamsLogoutMembers): Promise<AxiosResponse<boolean>> {
    return baseApi.post('organization/members/logout', params)
  }

  resetPasswordMembers(params: IParamsResetPasswordMembers): Promise<AxiosResponse<boolean>> {
    return baseApi.post('organization/members/reset', params)
  }

  updateMember(id: number, params: IParamsUpdateMember): Promise<AxiosResponse<IResponseUser>> {
    return baseApi.put(`organization/members/${id}`, params)
  }

  getMember(id: number): Promise<AxiosResponse<IResponseUser>> {
    return baseApi.get(`organization/members/${id}`)
  }

  createInvites(params: IParamsCreateInvites): Promise<AxiosResponse> {
    return baseApi.post('organization/invites', params)
  }

  rejectAccessRequest(id: number): Promise<AxiosResponse> {
    return baseApi.delete(`users/access-requests/${id}`)
  }

  logoutOtherDevices() {
    return baseApi.post('auth/api/v2/internal/user-sessions/logout-other-devices')
  }

  updateEmail(params: IParamsUpdateEmail): Promise<AxiosResponse> {
    return baseApi.post('users/email', params)
  }

  updatePassword(params: IParamsUpdatePassword): Promise<AxiosResponse> {
    return baseApi.post('users/password', params)
  }

  assumeUser(id: number): Promise<AxiosResponse<IResponseAssumeUser>> {
    return baseApi.post(`${API_URL_MICROSERVICE}users/assume/${id}`)
  }

  updateChangeRoleRequest(): Promise<AxiosResponse> {
    return baseApi.post('users/change-role-request')
  }
}

export const UsersApi = new Api()
